import ActionButton from "controls/global/action-button";
import React, {
  ReactNode,
  useEffect,
  useState,
} from "react";
import { borderRadius, colors, fontSize, fontWeight, padding } from "theme/defaultStyle";
import { DialogType } from "utils/custom-hooks/useDialogStyles";
import {
  StewartDialog,
  StewartDialogActions,
  StewartDialogContent,
} from "../stewart-dialog/StewartDialog";
import { IconButton, styled } from "@mui/material";
import { Box } from "@mui/system";
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from "@mui/icons-material/Close";

const StyledStewartDialog = styled(StewartDialog)(
  {
    "& .MuiPaper-root": {
      width: "545px !important",
      maxWidth: "545px !important",
      "& .MuiDialogActions-root": {
        justifyContent: "end",
        padding: "24px 40px 40px 40px",
        "& button.ActionButton-outlinedPrimary": {
          marginLeft: "16px"
        }
      }

    },
  });

const CustomDialogContent = styled(StewartDialogContent)({
  maxHeight: "70vh",
  overflowY: "auto",
  overflowWrap: "break-word",
  padding: "5px 36px 0px !important",
  "& .icon-box": {
    backgroundImage: 'linear-gradient(rgb(255, 196, 0), rgb(255, 171, 0))',
    color: colors.grey08,
    display: "flex",
    fontFamily: "Roboto",
    fontSize: fontSize.xlarge0,
    fontWeight: fontWeight.normal1,
    justifyContent: "center",
    float: "left",
    border: "1px solid transparent",
    borderRadius: "12px",
    "& svg": {
      fontSize: "30px",
      color: "rgb(255, 255, 255)",
      margin: "15px",
    }
  },
  ".message-box": {
    position: "relative",
    left: "24px",
    textAlign: "left",
    marginRight: "54px",
    fontSize: "22px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal",
  },
  // Used instead of the floated layout above when a `title` is supplied. A two-column
  // grid puts the icon beside the title and the body underneath it, so the icon stays
  // centered on the title and the body lines up with the title's left edge however long
  // either one runs — no float wrapping, no magic offsets.
  "& .titled-box": {
    alignItems: "center",
    columnGap: padding.xlarge1,
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    // 36px of content padding plus this lines the body up with the 40px right padding on
    // DialogActions, so the message ends level with the Done button.
    marginRight: "4px",
    "& .icon-box": {
      float: "none",
      gridColumn: 1,
      gridRow: 1,
    },
    "& .title-box": {
      fontSize: fontSize.xlarge2,
      fontStyle: "normal",
      fontWeight: fontWeight.bold2,
      gridColumn: 2,
      gridRow: 1,
      lineHeight: "normal",
      // The inset the body no longer needs still applies here: the close button sits
      // 36-68px from the right edge, level with this row. 36 + 4 + 50 clears it.
      marginRight: "50px",
      textAlign: "left",
    },
    // Set explicitly rather than inheriting StewartDialogContent's 20px, which would
    // leave the body competing with the heading instead of sitting under it.
    "& .body-box": {
      fontSize: fontSize.large,
      fontWeight: fontWeight.normal1,
      gridColumn: 2,
      gridRow: 2,
      lineHeight: 1.5,
      paddingTop: padding.large1,
      textAlign: "left",
    },
  },
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  zIndex: 1,
  position: "relative",
  top: "35px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
  // The titled layout follows the newer design, where the close glyph is near-black
  // rather than the blue the delete confirmations use.
  "&.titled .MuiSvgIcon-root": {
    color: colors.grey08,
  },
});

type Props = {
  singleActionButton?: boolean;
  singleActionButtonText?: string;
  // Defaults to the delete icon this dialog was introduced for; pass another when the
  // dialog is not confirming a deletion.
  icon?: ReactNode;
  // Supplying a title switches the header to a heading beside the icon with the message
  // as body copy beneath it. Without one the message itself is the heading.
  title?: ReactNode;
  confirmationMessage: string | ReactNode;
  isOpen: boolean;
  onYes: any;
  disableYes?: boolean;
  onNo?: any;
  disableNo?: boolean;
  buttonNoText?: string;
  cancelActionButton?: boolean;
  noActionButton?: boolean;
  onCancel?: any;
  type?: DialogType;
  width?: number;
};

const ConfirmationDialogV3 = ({
  singleActionButton,
  singleActionButtonText,
  icon = <DeleteIcon />,
  title,
  confirmationMessage,
  isOpen,
  onYes,
  disableYes = false,
  onNo,
  disableNo = false,
  buttonNoText,
  cancelActionButton,
  noActionButton = !singleActionButton,
  onCancel,
  type = "WARNING",
  width = 500,
}: Props) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <StyledStewartDialog
      open={open}
      width={width}
    >
      <CustomDialogContent type={type}>
        {cancelActionButton && (
          <CloseIconContainer className={title ? "titled" : undefined}>
            <IconButton className="iconButton" onClick={onCancel}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        )}
        {title ? (
          <Box className="titled-box">
            <Box className="icon-box">{icon}</Box>
            <Box className="title-box">{title}</Box>
            <Box className="body-box">{confirmationMessage}</Box>
          </Box>
        ) : (
          <>
            <Box className="icon-box">{icon}</Box>
            <Box className="message-box">{confirmationMessage}</Box>
          </>
        )}
      </CustomDialogContent>
      <StewartDialogActions>
        {!singleActionButton && (
          <>
            {noActionButton && (
              <ActionButton
                variant="outlined"
                color="secondary"
                onClick={onNo}
                disabled={disableNo}
              >
                {buttonNoText || "No"}
              </ActionButton>
            )}
          </>
        )}
        <ActionButton
          autoFocus
          variant="outlined"
          color="primary"
          onClick={onYes}
          disabled={disableYes}
        >
          {singleActionButtonText || "Yes"}
        </ActionButton>
      </StewartDialogActions>
    </StyledStewartDialog>
  );
};

export default ConfirmationDialogV3;
